import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import FNOLDashboard from "./pages/FNOL/FNOLPage";
import ToranaDashboard from "./dashboard/pages/Dashboard/Dashboard";
import LoginPage from "./pages/Login/LoginPage";
import OTPPage from "./pages/Login/OTPPage";
import { isAuthenticated, clearSession } from "./services/authService";
import sseService from "./services/sseService";
import SSEConnectionStatus from "./components/SSEConnectionStatus";
import "./responsive.css";

const SESSION_CHECK_MS = 30 * 60 * 1000; // 30 minutes

// Hook: resolves async isAuthenticated() once on mount
function useAuthCheck() {
  const [state, setState] = useState({ checked: false, authenticated: false });
  useEffect(() => {
    isAuthenticated().then((auth) => setState({ checked: true, authenticated: auth }));
  }, []);
  return state;
}

// Watcher: checks session every 30 min inside the Router context
function SessionWatcher() {
  const navigate = useNavigate();
  useEffect(() => {
    const interval = setInterval(async () => {
      const auth = await isAuthenticated();
      if (!auth) {
        clearSession();
        navigate("/login", { replace: true });
      }
    }, SESSION_CHECK_MS);
    return () => clearInterval(interval);
  }, [navigate]);
  return null;
}

// Blocks render until auth is resolved, then redirects if needed
const ProtectedRoute = ({ element }) => {
  const { checked, authenticated } = useAuthCheck();
  if (!checked) return null;
  return authenticated ? element : <Navigate to="/login" replace />;
};

const HomeRoute = () => {
  const { checked, authenticated } = useAuthCheck();
  if (!checked) return null;
  return authenticated ? <FNOLDashboard /> : <Navigate to="/login" replace />;
};

const GuestRoute = ({ element }) => {
  const { checked, authenticated } = useAuthCheck();
  if (!checked) return null;
  return authenticated ? <Navigate to="/" replace /> : element;
};

function App() {
  useEffect(() => {
    console.log('🚀 App: Initializing SSE connection...');

    // Connect to SSE endpoint
    sseService.connect();

    // Request notification permission
    sseService.requestNotificationPermission().then(permission => {
      console.log('📢 Notification permission:', permission);
    });

    // Cleanup on unmount
    return () => {
      console.log('👋 App: Cleaning up SSE connection...');
      sseService.disconnect();
    };
  }, []);

  return (
    <Router>
      {/* SSE Connection Status - visible on all pages */}
      <SSEConnectionStatus />
      {/* Session expiry watcher - checks every 30 min */}
      <SessionWatcher />

      <Routes>
        {/* Routes with FNOL ID and page type */}
        <Route path="/dashboard" element={<ProtectedRoute element={<ToranaDashboard />} />} />
        <Route path="/:fnolId/datahandling" element={<ProtectedRoute element={<FNOLDashboard />} />} />
        <Route path="/:fnolId/validation" element={<ProtectedRoute element={<FNOLDashboard />} />} />
        <Route path="/:fnolId/fraud-analysis" element={<ProtectedRoute element={<FNOLDashboard />} />} />
        <Route path="/:fnolId/triaging" element={<ProtectedRoute element={<FNOLDashboard />} />} />
        <Route path="/:fnolId/claims-setup" element={<ProtectedRoute element={<FNOLDashboard />} />} />

        {/* Dynamic route for specific FNOL ID - defaults to datahandling */}
        <Route path="/:fnolId" element={<ProtectedRoute element={<FNOLDashboard />} />} />

        {/* Auth routes - only accessible when NOT logged in */}
        <Route path="/login" element={<GuestRoute element={<LoginPage />} />} />
        <Route path="/otp" element={<GuestRoute element={<OTPPage />} />} />

        {/* Default route - smart home */}
        <Route path="/" element={<HomeRoute />} />
      </Routes>
    </Router>
  );
}

export default App;